import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from "react";
import { ChevronDown, Pencil } from "lucide-react";
import { getAt } from "@shared-core/domain/object-ui/body-path";
import type {
  Command,
  CommandResult,
} from "@shared-core/domain/object-ui/contract";
import { useCommandRun } from "@shared-core/domain/object-ui/hooks";
import { useObjectManifest } from "@shared-core/domain/object-ui/manifest";
import {
  asksBeforeAnyInput,
  isQuietDryRun,
  panelFieldAmong,
  panelFieldOf,
} from "@shared-core/domain/object-ui/setting-editor";
import { useIsPhone } from "../../useIsPhone";
import { useKeyboardInset } from "../../useKeyboardInset";
import { Button } from "../primitives/button";
import { cn } from "../primitives/utils";
import { Popover, PopoverContent, PopoverTrigger } from "../primitives/popover";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../primitives/sheet";
import { endpointOf } from "./command-shell/CommandShell";
import { SimpleField } from "./command-shell/CommandFields";
import { EntityField } from "./command-shell/EntityField";
import type {
  CustomWidgetRegistry,
  KnownEntities,
} from "./command-shell/types";
import type { EntityOption } from "./EntityCombobox";

/**
 * PROVISIONAL width of a panel that holds a typeahead: the picker's list opens as wide as the panel, and a list of
 * people or orders needs more room than a text box does.
 */
const ENTITY_PANEL_WIDTH = "w-[min(32rem,calc(100vw-2rem))]";
const FIELD_PANEL_WIDTH = "w-80";

/** The control inside the panel's field: an input, a textarea or a combobox trigger (typeahead, select). */
const FIELD_CONTROL = "input, textarea, [role='combobox']";

/**
 * The edit button of a setting that holds a value, and the small panel it opens beside the fact. The panel holds the
 * command's one editable field; Save runs the dry run and, when it has nothing to say, applies the change at once and
 * closes. When the dry run has something to show (an error, a check, a choice, a confirmation, display) the panel grows
 * into the full command dialog, carrying what was typed. A command the panel cannot hold (several fields, a
 * destructive one, one that asks before it takes any input, one with a custom widget) opens the dialog straight away.
 * On a phone the panel is a bottom sheet.
 */
export function SettingEditButton({
  label,
  currentValue,
  command,
  widgets,
  onOpen,
  onSaved,
  placeholder,
  needsAttention = false,
}: {
  label: string;
  /**
   * A setting with no value yet draws as a visible picker reading this ("Choose…") instead of the pencil beside a
   * dash, so it looks like something to click.
   */
  placeholder?: string;
  /** The empty setting is what holds the record up: the picker is drawn in the amber attention tone. */
  needsAttention?: boolean;
  /** The fact as the page shows it now ("Jane Ellis"): what a typeahead calls the value it starts on. */
  currentValue: string;
  command: Command;
  widgets: CustomWidgetRegistry;
  /** Open the command's full dialog, starting from this body, with the names the panel showed for what was picked. */
  onOpen: (
    command: Command,
    initialBody?: Record<string, unknown>,
    knownEntities?: KnownEntities,
  ) => void;
  /** The panel applied the change: say so and read the page again. */
  onSaved: (result: CommandResult) => void;
}) {
  const index = useObjectManifest();
  const isPhone = useIsPhone();
  const keyboardInset = useKeyboardInset();
  const [isOpen, setOpen] = useState(false);
  // The panel handing over to the dialog must not give focus back to the pencil: the dialog is taking it.
  const isGrowing = useRef(false);

  const declared = index.commandOf(command.name);
  const usesPanel =
    command.enabled &&
    widgets[declared.id] === undefined &&
    panelFieldOf(declared) !== null;
  const ariaLabel = `Change ${label.toLowerCase()}`;

  const grow = (
    body?: Record<string, unknown>,
    knownEntities?: KnownEntities,
  ) => {
    isGrowing.current = true;
    setOpen(false);
    if (knownEntities) onOpen(command, body, knownEntities);
    else onOpen(command, body);
  };
  const saved = (result: CommandResult) => {
    setOpen(false);
    onSaved(result);
  };

  const form = (
    <SettingEditForm
      label={label}
      currentValue={currentValue}
      command={command}
      onClose={() => setOpen(false)}
      onGrow={grow}
      onSaved={saved}
    />
  );
  const changeOpen = (next: boolean) => {
    if (next) isGrowing.current = false;
    setOpen(next);
  };
  const keepFocusWhenGrowing = (event: Event) => {
    if (isGrowing.current) event.preventDefault();
  };
  // Focus is moved into the field by the form, not by the first tabbable the library finds.
  const focusFormYourself = (event: Event) => event.preventDefault();

  // The panel's trigger opens it by itself; a command the panel cannot hold opens its dialog from the button.
  const onPencilClick = usesPanel ? undefined : () => onOpen(command);
  const pencil = placeholder ? (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className={cn(
        "h-7 gap-1 px-2 font-normal",
        needsAttention &&
          "border-amber-600 text-amber-700 dark:border-amber-400 dark:text-amber-400",
      )}
      aria-label={ariaLabel}
      title={command.enabled ? command.label : (command.reason ?? undefined)}
      disabled={!command.enabled}
      {...(onPencilClick ? { onClick: onPencilClick } : {})}
    >
      {placeholder}
      <ChevronDown className="h-3.5 w-3.5" aria-hidden />
    </Button>
  ) : (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-6 w-6 self-center text-muted-foreground"
      aria-label={ariaLabel}
      title={command.enabled ? command.label : (command.reason ?? undefined)}
      disabled={!command.enabled}
      {...(onPencilClick ? { onClick: onPencilClick } : {})}
    >
      <Pencil className="h-3.5 w-3.5" />
    </Button>
  );

  if (!usesPanel) return pencil;

  if (isPhone) {
    const above: CSSProperties | undefined =
      keyboardInset > 0
        ? {
            bottom: keyboardInset,
            maxHeight: `calc(100dvh - ${keyboardInset}px - 1rem)`,
          }
        : undefined;
    return (
      <Sheet open={isOpen} onOpenChange={changeOpen}>
        <SheetTrigger asChild>{pencil}</SheetTrigger>
        <SheetContent
          side="bottom"
          className="max-h-[85dvh] overflow-y-auto"
          style={above}
          onOpenAutoFocus={focusFormYourself}
          onCloseAutoFocus={keepFocusWhenGrowing}
          aria-describedby={undefined}
        >
          <SheetHeader>
            <SheetTitle>{ariaLabel}</SheetTitle>
          </SheetHeader>
          <div className="px-4 pb-4">{isOpen && form}</div>
        </SheetContent>
      </Sheet>
    );
  }

  const field = panelFieldOf(declared);
  return (
    <Popover open={isOpen} onOpenChange={changeOpen}>
      <PopoverTrigger asChild>{pencil}</PopoverTrigger>
      <PopoverContent
        align="start"
        aria-label={ariaLabel}
        className={
          field?.kind === "entity" ? ENTITY_PANEL_WIDTH : FIELD_PANEL_WIDTH
        }
        onOpenAutoFocus={focusFormYourself}
        onCloseAutoFocus={keepFocusWhenGrowing}
      >
        {isOpen && form}
      </PopoverContent>
    </Popover>
  );
}

function sameValue(a: unknown, b: unknown): boolean {
  const text = (v: unknown) => (v === null || v === undefined ? "" : String(v));
  return text(a) === text(b);
}

/**
 * The panel's content, mounted only while it is open so its command run (the dry run, the typed value) starts with
 * the panel and goes with it.
 */
function SettingEditForm({
  label,
  currentValue,
  command,
  onClose,
  onGrow,
  onSaved,
}: {
  label: string;
  currentValue: string;
  command: Command;
  onClose: () => void;
  onGrow: (
    body?: Record<string, unknown>,
    knownEntities?: KnownEntities,
  ) => void;
  onSaved: (result: CommandResult) => void;
}) {
  const index = useObjectManifest();
  const declared = index.commandOf(command.name);
  const endpoint = endpointOf(command, declared);
  const { run, snapshot } = useCommandRun(endpoint);
  const isPhone = useIsPhone();
  const formRef = useRef<HTMLFormElement>(null);
  const [isSaving, setSaving] = useState(false);
  const isCommitting = useRef(false);
  const hasDecided = useRef(false);
  // What the record holds now, from the first dry run's prefill: Save waits until the value differs from it.
  const [original, setOriginal] = useState<{ value: unknown } | null>(null);
  // What the typeahead's last pick was called.
  const [picked, setPicked] = useState<EntityOption | null>(null);

  const { dryRun, body, errors, fields, status, isPending } = snapshot;
  const field = panelFieldAmong(fields);
  const path = field?.path ?? "";
  const value = getAt(body, path);
  const isReady = dryRun !== null;
  // Typing before the first dry run has answered counts too: Save then waits for the answer.
  const isChanged = !sameValue(value, original?.value);
  const isBusy = isSaving || status === "committing";
  // What the dialog starts from when the panel hands over: the typed value, if the person has changed it.
  const typedBody = (changed: boolean): Record<string, unknown> | undefined =>
    changed && path && value !== undefined && value !== null
      ? { [path]: value }
      : undefined;

  // The name the typeahead showed for the person's pick: the dialog shows it too, not the id the body sends.
  const typedNames = (): KnownEntities | undefined =>
    path && picked !== null && picked.id === value
      ? { [path]: picked }
      : undefined;

  const latest = useRef({ value, typedBody, typedNames, onGrow, onSaved });
  latest.current = { value, typedBody, typedNames, onGrow, onSaved };

  // The first dry run shows whether the panel can hold this command: it may ask something first (a choice, a
  // confirmation), or hide the field the manifest declared, so the dialog takes over before anything is typed.
  useEffect(() => {
    if (!dryRun || hasDecided.current) return;
    hasDecided.current = true;
    const held = dryRun.prefill?.[path];
    setOriginal({ value: held });
    if (asksBeforeAnyInput(dryRun) || !field) {
      console.info("object_ui.setting_editor.opened_dialog", {
        command: declared.id,
        reason: field ? "asks_first" : "no_single_field",
      });
      const { value: typedNow, typedBody, typedNames, onGrow } = latest.current;
      onGrow(typedBody(!sameValue(typedNow, held)), typedNames());
    }
  }, [dryRun, field, path, declared.id]);

  // Focus moves into the field when the panel opens. A typeahead cannot be searched until the dry run has fixed its
  // scope, so its field appears (and takes focus) when that answer lands; until then the form holds focus.
  const hasField = field !== null && (field.kind !== "entity" || isReady);
  useEffect(() => {
    const form = formRef.current;
    if (!form) return;
    // Focus already on something inside the panel (the person got there first) stays where it is.
    if (form.querySelector(":focus") !== null) return;
    (form.querySelector<HTMLElement>(FIELD_CONTROL) ?? form).focus();
  }, [hasField]);

  // Save: wait for the dry run of what is typed now, then apply a quiet one and grow into the dialog on any other.
  useEffect(() => {
    if (!isSaving || isCommitting.current || !dryRun) return;
    if (status === "check_failed") {
      setSaving(false);
      return;
    }
    if (isPending || status !== "checked") return;
    if (!isQuietDryRun(dryRun)) {
      console.info("object_ui.setting_editor.opened_dialog", {
        command: declared.id,
        reason: "dry_run_has_something_to_show",
      });
      latest.current.onGrow(
        latest.current.typedBody(true),
        latest.current.typedNames(),
      );
      return;
    }
    isCommitting.current = true;
    void run.commit().then((result) => {
      isCommitting.current = false;
      setSaving(false);
      if (result) latest.current.onSaved(result);
    });
  }, [isSaving, dryRun, status, isPending, run, declared.id]);

  const save = () => {
    if (!isChanged || isBusy) return;
    setSaving(true);
  };

  // Enter on a closed typeahead or select that already holds the new value saves it; otherwise it would only open
  // the list again. (Space and click still open it.)
  const onKeyDownCapture = (event: KeyboardEvent<HTMLFormElement>) => {
    const target = event.target as HTMLElement;
    if (
      event.key === "Enter" &&
      target.getAttribute("role") === "combobox" &&
      target.getAttribute("aria-expanded") !== "true" &&
      isChanged
    ) {
      event.preventDefault();
      event.stopPropagation();
      save();
    }
  };

  const fieldErrors = errors.filter(
    (e) => e.field === path || e.field.startsWith(`${path}.`),
  );
  // Errors with no input of their own show with the banner, once a commit has been refused.
  const otherErrors = snapshot.banner
    ? errors.filter((e) => !fieldErrors.includes(e))
    : [];
  let control = null;
  if (field?.kind === "entity") {
    control = isReady ? (
      <EntityField
        field={field}
        value={value}
        errors={fieldErrors}
        disabled={isBusy}
        create={null}
        listFilters={dryRun.field_hints[path]?.list_filters ?? null}
        listScope={dryRun.field_hints[path]?.list_scope ?? null}
        note={dryRun.field_hints[path]?.note ?? null}
        knownAs={
          typeof original?.value === "string" && original.value
            ? { id: original.value, label: currentValue }
            : null
        }
        onChange={(next, option) => {
          setPicked(option ?? null);
          run.setValue(path, next ?? undefined);
        }}
        onCreate={async () => null}
      />
    ) : (
      <p role="status" className="text-sm text-muted-foreground">
        Checking…
      </p>
    );
  } else if (field) {
    control = (
      <SimpleField
        field={field}
        value={value}
        errors={fieldErrors}
        disabled={isBusy}
        note={dryRun?.field_hints[path]?.note ?? null}
        onChange={(next, options) =>
          run.setValue(path, next === null ? undefined : next, options)
        }
      />
    );
  }

  return (
    <form
      ref={formRef}
      tabIndex={-1}
      className="space-y-3 outline-none"
      aria-label={`Change ${label.toLowerCase()}`}
      onSubmit={(event) => {
        event.preventDefault();
        save();
      }}
      onKeyDownCapture={onKeyDownCapture}
    >
      {snapshot.banner && (
        <div
          role="alert"
          className={
            snapshot.banner.tone === "warning"
              ? "rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-900/30 dark:text-amber-100"
              : "rounded-md bg-red-50 px-3 py-2 text-sm text-red-800 dark:bg-red-900/30 dark:text-red-100"
          }
        >
          <p>{snapshot.banner.message}</p>
          {otherErrors.length > 0 && (
            <ul>
              {otherErrors.map((e) => (
                <li key={`${e.field}-${e.code}`}>{e.message}</li>
              ))}
            </ul>
          )}
          {status === "check_failed" && (
            <button
              type="button"
              className="mt-1 text-sm underline"
              onClick={() => run.retry()}
            >
              Check again
            </button>
          )}
        </div>
      )}
      {control}
      <div
        className={
          isPhone
            ? "flex gap-2 [&>button]:h-10 [&>button]:flex-1"
            : "flex justify-end gap-2"
        }
      >
        <Button type="button" variant="outline" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          size="sm"
          disabled={!isChanged || isBusy}
          aria-busy={isBusy}
        >
          {isBusy ? "Saving…" : "Save"}
        </Button>
      </div>
    </form>
  );
}
